import React, { FC, memo, useState, useEffect, ReactElement } from 'react';
import Collapsible from 'react-collapsible';
import Icon, { Icons } from '../Icon';
import { ITechnicalSpecificationsData, ITechnicalSpecifications } from '.';
import { theme } from '../../styles/theme';
import { Row, Col } from '@components/Grid';
import { CONSUMPTION_KEY_WLTP, ELECTRIC_RANGE_KEY } from '../../constants/characteristics';
import { useTranslations } from '@hooks/useTranslations';
import { useTechSpec } from '@hooks/useTechSpec';
import { IConfigurableCar } from '../../interfaces/Car';
import { FUEL_TYPES, isBrandOV, isMarketGB } from '../../constants/main';
import { TriggerIcon } from './TriggerIcon';
import { useFeatureSwitchEnabled } from '@hooks/useFeatureSwitchEnabled';
import { FEATURES_LIST } from '../../context/featureSwitchApp';
import { getData } from '@components/CarOptionsInfoBox/helpers';

interface IProps {
    spec: ITechnicalSpecifications;
    carDetail: IConfigurableCar;
    initialOpenState: boolean;
}

export const TechFeatureSection: FC<IProps> = memo(({ spec, carDetail, initialOpenState }) => {
    const { t } = useTranslations();
    const { getConsumptionText, legalTextUrl, fuelLegalText } = useTechSpec(carDetail);
    const fuelLower = carDetail.fuel?.toLowerCase();
    const isHybrid = FUEL_TYPES.HYBRID.includes(fuelLower);
    const [isExpanded, setIsExpanded] = useState(false);
    useEffect(() => {
        if (isExpanded !== initialOpenState) setIsExpanded(initialOpenState);
    }, [initialOpenState]);

    const { key, category } = spec;
    let categoryLabel: string | ReactElement = category;
    const categoryKey: string | ReactElement = key;
    if (key === CONSUMPTION_KEY_WLTP) {
        categoryLabel = t('technicalFeatures.environmentalDetails.consumption.label');
    }

    const isCo2ImageEnabled = useFeatureSwitchEnabled(FEATURES_LIST.FEATURE_SWITCH_SHOW_CO2_IMAGE);

    const showCo2ImageSection =
        isCo2ImageEnabled && carDetail?.engine?.specs?.find((char: { key: string }) => char.key === 'co2BoardImage');

    const getCo2Image = () =>
        carDetail?.engine?.specs?.find((char: { key: string }) => char.key === 'co2BoardImage')?.value;

    const getCo2Value = () =>
        carDetail?.engine?.specs?.find((char: { key: string }) => char.key === 'co2Combined')?.value;

    const localElectricRange = getData(carDetail.engine.specs, ELECTRIC_RANGE_KEY, 'local_value');

    return (
        <div className="technicalWrapper">
            <Collapsible
                open={initialOpenState}
                onTriggerClosing={() => setIsExpanded(false)}
                onTriggerOpening={() => setIsExpanded(true)}
                trigger={
                    <div className="collapsibleTriggerHeader">
                        <h3 className="featureTitleWide">{categoryLabel}</h3>
                        {isBrandOV && !isMarketGB ? (
                            <TriggerIcon isExpanded={isExpanded} />
                        ) : (
                            <Icon name={Icons.ChevronDown} color={theme.colors.primary} height={10} />
                        )}
                    </div>
                }
                triggerDisabled={initialOpenState}
            >
                <Row>
                    <Col className="technicalFeaturesContainer">
                        {spec.data.map((data: ITechnicalSpecificationsData, i: number) => {
                            const { value, key } = data;
                            let { label } = data;
                            if (CONSUMPTION_KEY_WLTP === categoryKey) {
                                label = getConsumptionText(`consumption.${key}`);
                            }

                            if (!label.includes('undefined')) {
                                return (
                                    <Row key={i} className="technicalFeaturesModuleWrap">
                                        <Col md={6} lg={7} sm={9} xs={7}>
                                            <div className="technicalFeaturesModule">
                                                <span
                                                    className="featureList"
                                                    dangerouslySetInnerHTML={{ __html: label }}
                                                />
                                            </div>
                                        </Col>
                                        <Col md={6} lg={4} sm={3} xs={5} style={{ textAlign: 'right' }}>
                                            <div className="technicalFeaturesModule">
                                                <span className="featureItemList">
                                                    <b>
                                                        {isHybrid && key === ELECTRIC_RANGE_KEY
                                                            ? localElectricRange ?? value
                                                            : value}
                                                    </b>
                                                </span>
                                            </div>
                                        </Col>
                                    </Row>
                                );
                            }
                        })}
                        {CONSUMPTION_KEY_WLTP === categoryKey && showCo2ImageSection && (
                            <Row className="technicalFeaturesModuleWrap">
                                <Col md={6} lg={7} sm={9} xs={7}>
                                    <div className="technicalFeaturesModule">
                                        <span className="featureList">{t('configurator.label.co2')}</span>
                                    </div>
                                </Col>
                                <Col md={6} lg={4} sm={3} xs={5} style={{ textAlign: 'right' }}>
                                    <div className="technicalFeaturesModule">
                                        <span className="featureItemList">
                                            <img
                                                alt={`${t('configurator.label.co2')} ${getCo2Value()}`}
                                                className="featureImageList"
                                                src={getCo2Image() as string}
                                            />
                                        </span>
                                    </div>
                                </Col>
                            </Row>
                        )}
                    </Col>
                </Row>
                {categoryKey === CONSUMPTION_KEY_WLTP && (
                    <Row>
                        <Col xs={12} md={12} className="legalTextContainer" style={{ width: '100%' }}>
                            <span className="legalText">
                                <span>
                                    {fuelLegalText}
                                    <a href={legalTextUrl} target="">
                                        {legalTextUrl}
                                    </a>
                                    .
                                </span>
                            </span>
                        </Col>
                    </Row>
                )}
            </Collapsible>
        </div>
    );
});
TechFeatureSection.displayName = 'TechFeatureSection';
